import { graphql } from "gatsby"
import { getSrc } from "gatsby-plugin-image"
import { TEMPLATE_TYPES } from "gatsby-singita-theme/src/common/constants"
import HelmetWrapper from "gatsby-singita-theme/src/components/Helmet"
import Main from "gatsby-singita-theme/src/components/Main"
import { useSeo } from "gatsby-singita-theme/src/hooks"
import React, { useEffect, useMemo, useState } from "react"
import Layout from "../modules/Layout"

const DATA_MAP = {
  title: ["name"],
  menuLabel: ["menuLabel"],
  image: ({ layout }) => {
    if (layout && layout[0].__typename === "ContentfulLayoutHero") {
      if (layout[0] && layout[0]?.images?.[0])
        return getSrc(layout[0]?.images[0])
      if (layout[0] && layout[0]?.imageRef?.image)
        return getSrc(layout[0]?.imageRef?.image)
    }
    return null
  },
}

const PageTemplate = props => {
  const {
    data: { contentfulPages },
    pageContext,
  } = props

  const { slug } = pageContext

  const [canonicalUrl, setCanonicalUrl] = useState(pageContext?.canonicalUrl || null)

  // Sync canonical URL state when pageContext changes (client-side navigation)
  useEffect(() => {
    if (pageContext?.canonicalUrl && pageContext.canonicalUrl !== canonicalUrl) {
      setCanonicalUrl(pageContext.canonicalUrl)
    }
  }, [pageContext?.canonicalUrl, canonicalUrl])

  const rawSeoData = useSeo(contentfulPages, DATA_MAP, contentfulPages.slug === "home" ? TEMPLATE_TYPES.HOME : TEMPLATE_TYPES.PAGE)
  const seoData = useMemo(() => ({
    ...rawSeoData,
    url: canonicalUrl || rawSeoData.url,
  }), [rawSeoData, canonicalUrl])

  const codes =
    contentfulPages && contentfulPages.connectedEntry && contentfulPages.connectedEntry.code
      ? { region: contentfulPages.connectedEntry.code }
      : {}

  return (
    <Main {...props} codes={codes}>
      <HelmetWrapper {...seoData} />
      <Layout
        layout={contentfulPages.layout}
        slug={slug}
        codes={codes}
        pageContext={{ ...pageContext, canonicalUrl, setCanonicalUrl }}
      />
    </Main>
  )
}

export default PageTemplate

export const pageQuery = graphql`
  query pageQuery($contentfulSlug: String!, $locale: String) {
    contentfulPages(slug: { eq: $contentfulSlug }, node_locale: { eq: $locale }) {
      id
      slug
      name
      structuredData {
        headline
        menuLabel
        description {
          description
        }
        image {
          # fluid(quality: 90, maxWidth: 800) {
          #   ...GatsbyContentfulFluid_withWebp_noBase64
          # }
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            formats: [AUTO, WEBP]
            quality: 90
            width: 800
          )
        }
        altText
        noIndex
        noFollow
      }
      connectedEntry {
        __typename
        ... on ContentfulRegion {
          code
        }
      }
      layout {
        __typename
        ... on Node {
          __typename
          ... on ContentfulContent {
            ...Content
          }
          ... on ContentfulLayoutGallery {
            ...GalleryFields
          }
          ... on ContentfulLayoutSection {
            ...SectionFields
          }
          ... on ContentfulLayoutProjectsGrid {
            ...ProjectsGridFields
          }
          ... on ContentfulLayoutHero {
            ...HeroFields
          }
          ... on ContentfulLayoutSectionLeftRight {
            ...SectionLeftRightFields
          }
          ... on ContentfulLayoutSectionGrid {
            ...SectionGridFields
          }
          ... on ContentfulLayoutSectionGridLodges {
            ...SectionGridLodgesFields
          }
          ... on ContentfulLayoutSectionGridLodgesPrivateVillas {
            ...SectionGridLodgesPrivateVillasFields
          }
          ... on ContentfulLayoutSectionGridLodgesRates {
            ...SectionGridLayoutLodgesRatesFields
          }
          ... on ContentfulLayoutCardHero {
            ...CardHeroFields
          }
          ... on ContentfulLayoutArchive {
            ...ArchiveFields
          }
          ... on ContentfulLayoutBanner {
            ...BannerFields
          }
          ... on ContentfulLayoutList {
            ...ListFields
          }
          ... on ContentfulAlert {
            ...AlertFields
          }
          ... on ContentfulActionbar {
            ...ActionBarFields
          }
          ... on ContentfulLayoutHeroVideo {
            ...HeroVideoFields
          }
          ... on ContentfulLayoutFeaturedArticles {
            ...FeaturedArticlesFields
          }
          ... on ContentfulLayoutBorder {
            ...BorderFields
          }
          ... on ContentfulLayoutContent {
            ...LayoutContentFields
          }
          ... on ContentfulActionbarBookingForm {
            ...ActionbarBookingFields
          }
        }
      }
    }
  }
`
